'use client';

import Image from 'next/image';
import { siteChrome } from '@/lib/site-chrome';

const { brand } = siteChrome;

export const ERROR_SCREEN_COPY = {
  eyebrow: 'Something went wrong',
  heading: 'This page hit a snag.',
  body: 'It was us, not you. Try again, or call us and a planner will take it from here.',
  retry: 'Try again',
  home: 'Go to the homepage',
} as const;

export interface ErrorScreenProps {
  /** Re-renders the page (Next's reset) or reloads it. */
  onRetry: () => void;
}

/**
 * The branded crash screen, shared by the route error page and the global one.
 * Self-contained (no header or footer, which could be what crashed), with the
 * phone number so a visitor who came to book can still reach us.
 */
export function ErrorScreen({ onRetry }: ErrorScreenProps) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-mist px-6 py-16">
      <div className="w-full max-w-lg rounded-3xl bg-white p-8 text-center shadow-sm sm:p-10">
        <a href="/" className="inline-block">
          <Image src={brand.logo.src} alt="Best Corporate Events" width={brand.logo.width} height={brand.logo.height} className="mx-auto h-10 w-auto" />
        </a>
        <p className="eyebrow mt-8 text-brand-royal">{ERROR_SCREEN_COPY.eyebrow}</p>
        <h1 className="mt-2 text-3xl font-extrabold text-brand-navy">{ERROR_SCREEN_COPY.heading}</h1>
        <p className="mt-3 leading-relaxed text-brand-navy/75">{ERROR_SCREEN_COPY.body}</p>
        <a href={brand.phoneHref} className="mt-6 inline-block text-2xl font-extrabold text-brand-royal hover:text-brand-navy">
          {brand.phone}
        </a>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            className="rounded-full bg-brand-yellow px-7 py-3 font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-royal"
          >
            {ERROR_SCREEN_COPY.retry}
          </button>
          <a
            href="/"
            className="rounded-full border-2 border-brand-navy/15 px-7 py-3 font-semibold text-brand-navy transition-colors hover:border-brand-royal hover:text-brand-royal"
          >
            {ERROR_SCREEN_COPY.home}
          </a>
        </div>
      </div>
    </main>
  );
}
